import { type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Byline } from "@/components/Byline";
import { breadcrumbScript, ORG_ID, WEBSITE_ID, SITE } from "@/lib/seo";

/**
 * Dedicated article route (takes precedence over /blog/$slug) because this post
 * needs an FAQ block, checklist, byline and Article + FAQPage schema that the
 * shared article template does not render. Listing metadata lives in
 * src/lib/blog-posts.ts so the blog index and sitemap pick it up as usual.
 */

const SLUG = "ico-confidential-waste-disposal-guide";
const URL = `${SITE}/blog/${SLUG}`;
const TITLE_TAG = "ICO Confidential Waste Disposal Guide | Preserve Earth";
const META_DESCRIPTION = "What the ICO expects when you dispose of confidential paper and old devices: locked bins, shredding, certificates, disposal logs and the 72 hour rule.";
const H1 = "What does the ICO expect when you dispose of confidential waste?";
const EXCERPT = "A plain-English guide to what the ICO expects when you dispose of confidential paper and old devices, from locked bins and cross-cut shredding to certificates of destruction, disposal logs and the 72 hour breach reporting window.";
const HERO_SRC = "/images/blog/ico-confidential-waste-hero.webp";
const HERO_URL = `${SITE}${HERO_SRC}`;
const HERO_WIDTH = 1280;
const HERO_HEIGHT = 720;
const HERO_ALT = "Practice staff member placing patient paperwork into a locked confidential waste bin, with a certificate of destruction on the desk";
const LOGO_URL = `${SITE}/images/brand/preserve-earth-logo-navy.png`;
const DATE_PUBLISHED = "2026-10-19";
const DATE_MODIFIED = "2026-10-19";
const AUTHOR = "Preserve Earth team";
const QUOTE_HREF = "/services/confidential-waste-disposal#enquire";

type InlineLink = { text: string; href: string };
type Para = { v: string; links: InlineLink[] };
type Section = { id: string; h2: string; answer: string; more: Para[] };

const CONTENT: {
  intro: Para[];
  sections: Section[];
  checklistH2: string;
  checklistAnswer: string;
  checklist: string[];
  faqH2: string;
  faqAnswer: string;
  faq: { q: string; a: string }[];
  ctaH2: string;
  ctaText: string;
  ctaButton: string;
  disclaimer: string;
  sources: { label: string; href: string }[];
} = {
  "intro": [
    {
      "v": "Patient notes, care plans, safeguarding records, donor forms and old staff laptops all hold personal information. When they reach the end of their life, how you dispose of them matters. The ICO's audit checklists set out what it expects, and the same steps apply whether you run a dental practice in Birmingham or a charity anywhere else in the UK.",
      "links": []
    },
    {
      "v": "Below we summarise the ICO's records management and security checklist and its disposal and deletion checklist, using UK GDPR framing, alongside recent analysis of paper disposal breaches reported to the ICO.",
      "links": [
        {
          "text": "records management and security checklist",
          "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-management-and-security"
        },
        {
          "text": "disposal and deletion checklist",
          "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/"
        }
      ]
    }
  ],
  "sections": [
    {
      "id": "paper",
      "h2": "How should you dispose of confidential paper records?",
      "answer": "Put paper records containing personal information into locked waste bins or consoles, then have them destroyed by cross-cut shredding or incineration, either in-house or through a third party. The ICO also expects you to document your disposal methods and to store records awaiting destruction securely, for example in a locked area with restricted access.",
      "more": [
        {
          "v": "The ICO's records management and security checklist says that for paper documents you should use locked waste bins for records containing personal information, with in-house or third-party cross shredding or incineration in place. Its disposal and deletion checklist gives cross-cut or micro-cut shredding as examples of secure methods, and suggests checking areas holding records awaiting destruction on site walks.",
          "links": [
            {
              "text": "records management and security checklist",
              "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-management-and-security"
            },
            {
              "text": "disposal and deletion checklist",
              "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/"
            }
          ]
        },
        {
          "v": "If you shred on site, Fellowes notes that for most personal data a cross-cut or micro-cut shredder at DIN 66399 security level P-4 or above is appropriate. For regular volumes, our confidential waste disposal service covers locked consoles and scheduled collections, and our one-off shredding service suits an archive clear-out.",
          "links": [
            {
              "text": "Fellowes notes",
              "href": "https://datacentreinsight.co.uk/2026/09/03/more-than-a-third-of-uk-paper-disposal-breaches-not-reported-to-the-ico-within-the-legal-72-hour-window/"
            },
            {
              "text": "confidential waste disposal service",
              "href": "/services/confidential-waste-disposal"
            },
            {
              "text": "one-off shredding service",
              "href": "/services/one-off-shredding"
            }
          ]
        }
      ]
    },
    {
      "id": "certificate",
      "h2": "Do you need a certificate of destruction?",
      "answer": "If a third party disposes of personal information for you, you need evidence that it was destroyed. The ICO expects contracts that give you appropriate assurance of secure disposal, for example through audit checks and destruction certificates, and suggests assigning a staff member to check that certificates match what you sent for destruction.",
      "more": [
        {
          "v": "The ICO's disposal and deletion checklist also expects contracts with disposal providers to be signed by an appropriate senior manager and to include security measures, accountability and your right to audit the provider. Without evidence of disposal, it warns, personal information may not be secure during disposal or may not be disposed of at all, which may breach UK GDPR article 5(2).",
          "links": [
            {
              "text": "disposal and deletion checklist",
              "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/"
            }
          ]
        },
        {
          "v": "Every collection comes with an electronic note of secure destruction for your records. The ICO suggests storing electronic confirmations of destruction in a dedicated folder or email inbox.",
          "links": []
        }
      ]
    },
    {
      "id": "log",
      "h2": "Why should you keep a disposal log?",
      "answer": "A disposal log shows what left your control, when and where it went. The ICO expects a log of all equipment and confidential waste sent for disposal or destruction, plus a log of records and devices awaiting destruction and their location. Matching that log against your certificates of destruction gives you a clear evidence trail.",
      "more": [
        {
          "v": "The ICO also expects you to destroy records in line with a retention schedule, and to obtain and log management approval before destroying them. If records are not destroyed in line with the schedule, it says you should report the failure as an incident and take appropriate action.",
          "links": [
            {
              "text": "retention schedule",
              "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-management-and-security"
            }
          ]
        }
      ]
    },
    {
      "id": "devices",
      "h2": "How should you dispose of old laptops and other devices?",
      "answer": "Use a secure method such as device wiping, degaussing or hardware shredding, and document it. Until collection, the ICO expects devices awaiting destruction to be stored securely, for example in a locked area with restricted access, with a log of every device awaiting destruction and its location.",
      "more": [
        {
          "v": "The ICO also suggests keeping an access log showing who has accessed devices awaiting destruction, checking those areas on site walks, and using a third-party secure hardware destruction provider. If personal information on electronic devices is not destroyed securely, it warns, the information may be recoverable.",
          "links": [
            {
              "text": "suggests keeping an access log",
              "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/"
            }
          ]
        },
        {
          "v": "Our IT and media recycling service collects laptops, PCs, hard drives and other IT equipment for secure destruction. If you are also replacing branded uniforms or PPE, ask about our uniform disposal service at the same time. See why old devices should never go in the bin.",
          "links": [
            {
              "text": "IT and media recycling service",
              "href": "/services/it-and-media-recycling"
            },
            {
              "text": "uniform disposal service",
              "href": "/services/textile-recycling"
            },
            {
              "text": "why old devices should never go in the bin",
              "href": "/blog/office-laptops-lithium-batteries-safe-disposal"
            }
          ]
        }
      ]
    },
    {
      "id": "sectors",
      "h2": "Which organisations report the most paper disposal breaches?",
      "answer": "Health organisations top the list. Fellowes analysed ICO data on 846 paper disposal breaches reported by UK organisations between 2019 and 2025. Health accounted for 227 (26.83%), followed by education and childcare with 185 (21.87%), local government with 69, social care with 67 and charitable and voluntary organisations with 62.",
      "more": [
        {
          "v": "Together, those five sectors account for more than 72% of all reported incidents. The analysis describes the breaches as paperwork containing personal data being disposed of without being shredded or otherwise destroyed, leaving personal information identifiable. See a recent example from Zagreb.",
          "links": [
            {
              "text": "The analysis",
              "href": "https://datacentreinsight.co.uk/2026/09/03/more-than-a-third-of-uk-paper-disposal-breaches-not-reported-to-the-ico-within-the-legal-72-hour-window/"
            },
            {
              "text": "a recent example from Zagreb",
              "href": "/blog/confidential-paper-binned-by-mistake-zagreb-lesson"
            }
          ]
        }
      ]
    },
    {
      "id": "72-hours",
      "h2": "How quickly must you report a confidential waste breach?",
      "answer": "Under Article 33 of the UK GDPR, organisations are required to report personal data breaches to the ICO within 72 hours of becoming aware of them. Yet in the Fellowes analysis, more than a third of paper disposal breaches were reported late: 208 between 72 hours and one week, and 103 after more than a week.",
      "more": [
        {
          "v": "In total, 63.24% of incidents in the dataset were reported within the 72 hour window. The analysis notes that delayed reporting can complicate the response for affected individuals and may indicate that internal detection or escalation procedures are not operating effectively. Fellowes recommends a clear, written disposal policy communicated to all staff who handle such records, backed by staff training.",
          "links": [
            {
              "text": "The analysis notes",
              "href": "https://datacentreinsight.co.uk/2026/09/03/more-than-a-third-of-uk-paper-disposal-breaches-not-reported-to-the-ico-within-the-legal-72-hour-window/"
            }
          ]
        }
      ]
    }
  ],
  "checklistH2": "What should a confidential waste disposal checklist include?",
  "checklistAnswer": "Use this short checklist to review how your practice, nursery, care service or charity handles confidential paper and old devices. Each point maps to an expectation in the ICO's records management and disposal checklists, so it also works as a quick way to spot gaps before an audit or inspection.",
  "checklist": [
    "Locked confidential waste bins or consoles wherever personal information is handled.",
    "Cross-cut or micro-cut shredding, or incineration, set out in your disposal policy.",
    "A retention schedule, with management approval logged before records are destroyed.",
    "A disposal log of all equipment and confidential waste sent for destruction.",
    "Old devices wiped, degaussed or physically destroyed, with the method recorded.",
    "Secure, restricted-access storage for paper and devices awaiting collection, plus a log of what is stored and where.",
    "A contract with your disposal provider covering security measures, accountability and your right to audit.",
    "A certificate of destruction for every collection, checked against your log by a named staff member.",
    "A breach process that lets you report to the ICO within 72 hours of becoming aware of a breach."
  ],
  "faqH2": "What else do organisations ask about ICO disposal rules?",
  "faqAnswer": "Here are short answers to common questions about disposing of confidential waste. One point to note first: the ICO says that, due to changes made by the Data (Use and Access) Act, its audit guidance is under review and may be subject to change, so check the current version before you update your own policy.",
  "faq": [
    {
      "q": "Does the ICO require cross-cut shredding?",
      "a": "The ICO's disposal and deletion checklist gives cross-cut or micro-cut shredding as examples of secure disposal methods for physical records, and its records management checklist refers to in-house or third-party cross shredding or incineration. The aim is to prevent disclosure before, during and after disposal."
    },
    {
      "q": "How long do we have to report a confidential waste breach?",
      "a": "Under Article 33 of the UK GDPR, organisations are required to report personal data breaches to the ICO within 72 hours of becoming aware of them."
    },
    {
      "q": "Who should check our certificates of destruction?",
      "a": "The ICO suggests assigning a staff member to check that destruction certificates match what you sent for destruction, and storing electronic confirmations of destruction in a dedicated folder or email inbox."
    },
    {
      "q": "Can we keep old laptops in a cupboard until they are collected?",
      "a": "Only if the storage is secure. The ICO expects devices awaiting destruction to be stored securely, for example in a locked area with restricted access, with a log of all devices awaiting destruction and their location."
    },
    {
      "q": "Do you provide proof of destruction?",
      "a": "Yes. Every confidential shredding and IT disposal collection comes with an electronic note of secure destruction for your records, so you can match it against your disposal log."
    }
  ],
  "ctaH2": "Need secure confidential shredding or IT disposal?",
  "ctaText": "We collect confidential paper and old IT equipment from clinics, dentists, nurseries, care providers and charities in Birmingham and across the UK. Every collection comes with an electronic note of secure destruction, ready to match against your disposal log. Tell us what you need and we will come back to you with a quote.",
  "ctaButton": "Request a quote",
  "disclaimer": "This article is general guidance based on ICO checklists and published analysis. It is not legal advice. For advice on your own obligations, refer to the ICO's current guidance or a qualified adviser.",
  "sources": [
    {
      "label": "ICO: Records management and security checklist",
      "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-management-and-security"
    },
    {
      "label": "ICO: Disposal and deletion checklist",
      "href": "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/"
    },
    {
      "label": "Data Centre Insight: Fellowes analysis of ICO paper disposal breach data (3 September 2026)",
      "href": "https://datacentreinsight.co.uk/2026/09/03/more-than-a-third-of-uk-paper-disposal-breaches-not-reported-to-the-ico-within-the-legal-72-hour-window/"
    }
  ]
};

const crumbs = [
  { name: "Blog", path: "/blog" },
  { name: H1, path: `/blog/${SLUG}` },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: H1,
  description: META_DESCRIPTION,
  image: { "@type": "ImageObject", url: HERO_URL, width: HERO_WIDTH, height: HERO_HEIGHT },
  datePublished: "2026-10-19T08:00:00+01:00",
  dateModified: "2026-10-19T08:00:00+01:00",
  inLanguage: "en-GB",
  author: { "@type": "Organization", name: AUTHOR, url: `${SITE}/` },
  publisher: {
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Preserve Earth",
    url: `${SITE}/`,
    logo: { "@type": "ImageObject", url: LOGO_URL },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": URL },
  isPartOf: { "@id": WEBSITE_ID },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: CONTENT.faq.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const Route = createFileRoute("/blog/ico-confidential-waste-disposal-guide")({
  head: () => ({
    meta: [
      { title: TITLE_TAG },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: TITLE_TAG },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: HERO_URL },
      { property: "og:image:width", content: String(HERO_WIDTH) },
      { property: "og:image:height", content: String(HERO_HEIGHT) },
      { property: "og:image:alt", content: HERO_ALT },
      { property: "article:published_time", content: DATE_PUBLISHED },
      { property: "article:modified_time", content: DATE_MODIFIED },
      { property: "article:section", content: "Data Protection" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE_TAG },
      { name: "twitter:description", content: META_DESCRIPTION },
      { name: "twitter:image", content: HERO_URL },
      { name: "twitter:image:alt", content: HERO_ALT },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
      breadcrumbScript(crumbs),
    ],
  }),
  component: IcoConfidentialWastePage,
});

const linkClass =
  "underline decoration-moss/40 underline-offset-4 transition-colors hover:text-moss";

/** Wraps the first occurrence of each link text, in text order (same approach as the shared article renderer). */
function withLinks(p: Para): ReactNode {
  if (!p.links.length) return p.v;
  const out: ReactNode[] = [];
  let rest = p.v;
  let key = 0;
  const ordered = [...p.links].sort((a, b) => p.v.indexOf(a.text) - p.v.indexOf(b.text));
  for (const l of ordered) {
    const idx = rest.indexOf(l.text);
    if (idx === -1) continue;
    if (idx > 0) out.push(rest.slice(0, idx));
    const external = l.href.startsWith("https://");
    out.push(
      <a
        key={key++}
        href={l.href}
        className={linkClass}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {l.text}
      </a>,
    );
    rest = rest.slice(idx + l.text.length);
  }
  if (rest) out.push(rest);
  return out;
}

const h2Class = "mt-14 font-serif text-3xl leading-tight text-charcoal lg:text-4xl";
const answerClass = "mt-5 text-pretty text-lg leading-[1.75] text-charcoal";
const bodyClass = "mt-5 text-pretty text-base leading-[1.8] text-charcoal/80";

function IcoConfidentialWastePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="/#enquire"
      />

      <main>
        <article>
          <header className="container-page pt-16 pb-10">
            <Breadcrumbs crumbs={crumbs} />
            <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
              Data Protection · 6 min read
            </p>
            <h1 className="max-w-[28ch] text-balance font-serif text-4xl leading-[1.05] text-charcoal md:text-5xl lg:text-6xl">
              {H1}
            </h1>
            <p className="mt-8 max-w-[60ch] text-pretty text-lg leading-relaxed text-charcoal/70">
              {EXCERPT}
            </p>
            <Byline published={articleSchema.datePublished} modified={articleSchema.dateModified} />
          </header>

          <div className="container-page">
            <figure className="overflow-hidden rounded-md outline outline-1 -outline-offset-1 outline-charcoal/10">
              <img
                src={HERO_SRC}
                alt={HERO_ALT}
                width={HERO_WIDTH}
                height={HERO_HEIGHT}
                loading="eager"
                fetchPriority="high"
                className="aspect-[16/9] w-full object-cover"
              />
            </figure>
          </div>

          <div className="container-page py-20">
            <div className="grid gap-16 lg:grid-cols-12">
              <aside className="lg:col-span-3">
                <div className="sticky top-24 space-y-6 border-l border-moss/30 pl-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    The Ledger
                  </p>
                  <p className="text-sm leading-relaxed text-charcoal/70">
                    Field notes on compliant, documented disposal for UK businesses.
                  </p>
                  <Link
                    to="/blog"
                    className="block text-xs font-medium uppercase tracking-[0.2em] text-moss underline decoration-moss/30 underline-offset-4"
                  >
                    All articles
                  </Link>
                </div>
              </aside>

              <div className="lg:col-span-9 lg:max-w-[68ch]">
                {CONTENT.intro.map((p, i) => (
                  <p key={i} className={i === 0 ? "text-pretty text-base leading-[1.8] text-charcoal/80" : bodyClass}>
                    {withLinks(p)}
                  </p>
                ))}

                {CONTENT.sections.map((s) => (
                  <section key={s.id} id={s.id} aria-labelledby={`${s.id}-h`}>
                    <h2 id={`${s.id}-h`} className={h2Class}>
                      {s.h2}
                    </h2>
                    <p className={answerClass}>{s.answer}</p>
                    {s.more.map((p, i) => (
                      <p key={i} className={bodyClass}>
                        {withLinks(p)}
                      </p>
                    ))}
                  </section>
                ))}

                <section id="checklist" aria-labelledby="checklist-h">
                  <h2 id="checklist-h" className={h2Class}>
                    {CONTENT.checklistH2}
                  </h2>
                  <p className={answerClass}>{CONTENT.checklistAnswer}</p>
                  <ul className="mt-8 space-y-3 border-l border-moss/30 pl-6">
                    {CONTENT.checklist.map((item) => (
                      <li key={item} className="flex gap-3 text-base leading-[1.7] text-charcoal/80">
                        <span aria-hidden className="mt-[0.55em] h-2 w-2 shrink-0 rounded-sm border border-moss" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section id="faq" aria-labelledby="faq-h">
                  <h2 id="faq-h" className={h2Class}>
                    {CONTENT.faqH2}
                  </h2>
                  <p className={answerClass}>{CONTENT.faqAnswer}</p>
                  <div className="mt-8 divide-y divide-charcoal/10 border-y border-charcoal/10">
                    {CONTENT.faq.map((f) => (
                      <div key={f.q} className="py-6">
                        <h3 className="font-serif text-xl leading-snug text-moss">{f.q}</h3>
                        <p className="mt-3 text-base leading-[1.8] text-charcoal/80">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <aside
                  aria-labelledby="cta-h"
                  className="mt-20 border-y border-charcoal/15 bg-sage-light/40 px-8 py-10"
                >
                  <h2 id="cta-h" className="font-serif text-2xl text-charcoal">
                    {CONTENT.ctaH2}
                  </h2>
                  <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-charcoal/70">
                    {CONTENT.ctaText}
                  </p>
                  <a
                    href={QUOTE_HREF}
                    className="mt-6 inline-flex items-center gap-3 rounded-sm bg-moss px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-charcoal"
                  >
                    {CONTENT.ctaButton}
                  </a>
                </aside>

                <div className="mt-12 text-sm leading-relaxed text-charcoal/70">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">Sources</p>
                  <ul className="mt-3 space-y-2">
                    {CONTENT.sources.map((s) => (
                      <li key={s.href}>
                        <a href={s.href} target="_blank" rel="noopener noreferrer" className={linkClass}>
                          {s.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 italic">{CONTENT.disclaimer}</p>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}
